import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { Activity } from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/ui/data-table';
import { mcpRunApi } from '@/features/mcp/lib/mcp-run-api';
import { piecesHooks } from '@/features/pieces/lib/pieces-hook';
import {
  McpRun,
  McpRunStatus,
  SeekPage,
  assertNotNullOrUndefined,
} from '@activepieces/shared';

import McpRunDetails from './mcp-run-details';
import { mcpRunColumns, mcpRunFilters } from './mcp-run-utils';

export type FetchRunsParams = {
  cursor?: string;
  limit: number;
  metadata?: string;
  status?: McpRunStatus[];
};

type McpHistoryContentProps = {
  mcpId: string;
  fetchRuns: (params: FetchRunsParams) => Promise<SeekPage<McpRun>>;
};

export const McpHistoryContent = ({
  fetchRuns,
  mcpId,
}: McpHistoryContentProps) => {
  const [searchParams] = useSearchParams();
  const { metadata, isLoading: isPiecesLoading } =
    piecesHooks.useAllStepsMetadata({
      searchQuery: '',
      type: 'action',
    });

  const [selectedItem, setSelectedItem] = useState<McpRun | null>(null);

  const { data: runs, isLoading } = useQuery({
    queryKey: ['mcp-runs', mcpId, searchParams.toString()],
    queryFn: () => {
      const metadataFilter = searchParams.get('metadata');
      const statusParams = searchParams.getAll('status') as McpRunStatus[];
      const status = statusParams.length > 0 ? statusParams : undefined;
      const cursor = searchParams.get('cursorRequest');
      const limit = searchParams.get('limit')
        ? parseInt(searchParams.get('limit')!)
        : 10;

      return fetchRuns({
        cursor: cursor ?? undefined,
        limit,
        metadata: metadataFilter ?? undefined,
        status,
      });
    },
    enabled: !!mcpId,
  });

  const columns = useMemo(() => mcpRunColumns(metadata ?? []), [metadata]);
  const filters = useMemo(() => mcpRunFilters(), []);

  return (
    <div className="w-full ">
      <div className="flex items-center justify-between">
        <div className="space-y-0">
          <h1 className="text-xl font-semibold tracking-tight">
            {t('Tool Usage History')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('Track and monitor your MCP tool executions')}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        page={runs}
        isLoading={isLoading || isPiecesLoading}
        filters={filters}
        onRowClick={(row) => {
          setSelectedItem(row);
        }}
      />

      {selectedItem && (
        <McpRunDetails
          selectedItem={selectedItem}
          metadata={metadata ?? []}
          setSelectedItem={setSelectedItem}
        />
      )}
    </div>
  );
};

export const McpHistoryPage = () => {
  const { mcpId, projectId } = useParams();

  assertNotNullOrUndefined(mcpId, 'mcpId is not set');
  assertNotNullOrUndefined(projectId, 'projectId is not set');

  const fetchRuns = useCallback(
    (params: FetchRunsParams) =>
      mcpRunApi.list({
        projectId,
        mcpId,
        cursorRequest: params.cursor,
        limit: params.limit,
        metadata: params.metadata,
        status: params.status,
      }),
    [mcpId, projectId],
  );

  return <McpHistoryContent mcpId={mcpId} fetchRuns={fetchRuns} />;
};

McpHistoryPage.displayName = 'McpHistoryPage';

export default McpHistoryPage;